import { toast } from "react-toastify";
import { downloadFile, downloadFolder, logAccess } from "../utils/utils";
import {
  handleDelete,
  handleFavourites,
  handleRestore,
  handleNameSubmit,
  handleMove,
} from "../utils/fileActions";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useFileActions = ({ token, openModal, closeModal }) => {
  const queryClient = useQueryClient();

  const refreshRepo = () => {
    queryClient.invalidateQueries({ queryKey: ["folderContents"] });
    queryClient.invalidateQueries({ queryKey: ["favourites"] });
    queryClient.invalidateQueries({ queryKey: ["bin"] });
    queryClient.invalidateQueries({ queryKey: ["activityLogs"] });
    queryClient.invalidateQueries({ queryKey: ["recentAccessed"] });
    queryClient.invalidateQueries({ queryKey: ["homeCounts"] });
    queryClient.invalidateQueries({ queryKey: ["repo"] });
    queryClient.invalidateQueries({ queryKey: ["homeData"] });
  };

  /* ---------------- DELETE ---------------- */
  const deleteMutation = useMutation({
    mutationFn: ({ file, type }) => handleDelete(file, token, type),

    onSuccess: (_, variables) => {
      const msg =
        variables?.type === "soft"
          ? "File moved to bin successfully"
          : "File deleted permanently";

      toast.success(msg);
      refreshRepo();
      closeModal?.();
    },

    onError: () => {
      toast.error("Delete failed");
    },
  });

  const deleteFile = (file, type = "soft", setState) => {
    deleteMutation.mutate(
      { file, type },
      {
        onSuccess: () => {
          setState?.([]);
        },
      },
    );
  };

  /* ---------------- RESTORE ---------------- */
  const restoreMutation = useMutation({
    mutationFn: (file) => handleRestore(file, token),

    onSuccess: () => {
      toast.success("Restored successfully");
      refreshRepo();
    },
  });

  const restoreFile = (file, setState) => {
    restoreMutation.mutate(file, {
      onSuccess: () => setState?.([]),
    });
  };

  /* ---------------- FAVOURITE ---------------- */
const favouriteMutation = useMutation({
  mutationFn: (files) => {
    const payloads = Array.isArray(files) ? files : [files];
    return Promise.all(payloads.map((file) => handleFavourites(file, token)));
  },

  onSuccess: () => {
    toast.success("Updated favourites"); // fires once after all APIs resolve
    refreshRepo();
    closeModal?.();
  },

  onError: () => {
    toast.error("Favourite update failed");
  },
});

  const renameMutation = useMutation({
    mutationFn: ({ file, newName }) => handleNameSubmit(file, token, newName),

    onSuccess: () => {
      toast.success("Rename successful");
      refreshRepo();
      closeModal?.();
    },

    onError: () => {
      toast.error("Something went wrong, please try again later!");
    },
  });

 const moveMutation = useMutation({
  mutationFn: ({ file, destinationFolderId }) =>
    handleMove(file, token, destinationFolderId),

  onSuccess: () => {
    toast.success("File moved successfully");
    refreshRepo(); // use common refresher
    closeModal?.();
  },

  onError: (error) => {
    toast.error(
      error?.message || "Something went wrong while moving file"
    );
  },
});

const moveFile = (file, destinationFolderId, handleClose) => {
  moveMutation.mutate(
    { file, destinationFolderId },
    {
      onSuccess: () => {
        handleClose?.(); 
      },
    }
  );
};

  const renameFile = (file, newName, setState) => {
    const trimmedName = newName?.trim();

    // Validation
    if (!trimmedName) {
      toast.error("File name cannot be empty");
      return;
    }

    if (trimmedName === file?.file_name) {
      toast.error("New name must be different from current name");
      return;
    }

    if (trimmedName?.length > 255) {
      toast.error("File name cannot exceed 255 characters");
      return;
    }

    const invalidChars = /[\\/:*?"<>|]/;
    if (invalidChars.test(trimmedName)) {
      toast.error("File name contains invalid characters");
      return;
    }
    renameMutation.mutate(
      { file, newName },
      {
        onSuccess: () => {
          setState?.(""); // clear input
        },
      },
    );
  };

  const favourite = (file, setState) => {
    favouriteMutation.mutate(file, {
      onSuccess: () => setState?.([]),
    });
  };

  /* ---------------- DOWNLOAD ---------------- */
  const download = (file) => {
    downloadFile(file?.s3_url, file?.file_name);
  };

  const folderDownload = (folder) => {
    downloadFolder(token, folder?.id, folder?.folder_name);
  };

  /* ---------------- MODAL ACTIONS ---------------- */
  const openRename = (file) => openModal("rename", file);
  const openDelete = (file) => openModal("delete", file);
  const openMove = (file) => openModal("moveFile", file);

  /* ---------------- PREVIEW ---------------- */
  const preview = (file) => {
    openModal("filePreview", {
      ...file,
      preview_url: file?.s3_url,
      download_url: file?.s3_url,
      extension: file?.file_type,
      name: file?.file_name,
    });

    logAccess(token, "file", file?.id);
  };

  return {
    favourite,
    deleteFile,
    restoreFile,
    download,
    openRename,
    openDelete,
    openMove,
    preview,
    folderDownload,
    renameFile,
    moveFile,
    isMoving: moveMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRestoring: restoreMutation.isPending,
    isFavouriting: favouriteMutation.isPending,
    isRenaming: renameMutation.isPending,
  };
};
